import { skillLabel } from 'src/common/utils/skillNames';

export type ChatSkill = {
  id: string;
  name: string;
  description: string;
  // `firm` skills were set up by the firm; `builtin` ones are management entry points the
  // user's role allows, which carry their own display label.
  kind: 'firm' | 'builtin';
  label: string | null;
  // What the composer asks for once a built-in command is picked; the firm's skills share one.
  instructionPlaceholder?: string | null;
};

const CREATE_SKILL_NAME = 'skill_manager';

const COMPOSER_PLACEHOLDER = 'Ask me anything';
const COMPOSER_PLACEHOLDER_WITH_SKILLS = 'Ask me anything, or type / to use a skill';
const SKILL_INSTRUCTION_PLACEHOLDER = 'Add an instruction (optional)';

/** A picked skill can be sent on its own, so the box stops asking for a question. */
export function composerPlaceholder(skillsAvailable: boolean, selectedSkill: ChatSkill | null) {
  if (!skillsAvailable) return COMPOSER_PLACEHOLDER;
  if (!selectedSkill) return COMPOSER_PLACEHOLDER_WITH_SKILLS;
  return selectedSkill.instructionPlaceholder ?? SKILL_INSTRUCTION_PLACEHOLDER;
}

export type SlashCommandQuery = {
  query: string;
  start: number;
  end: number;
};

/**
 * The slash command under the caret: a `/` at the start of the message or after whitespace,
 * followed by the text typed so far. Null when the caret is not inside one.
 */
export function findSlashCommand(value: string, caret: number): SlashCommandQuery | null {
  const beforeCaret = value.slice(0, caret);
  const match = /(^|\s)\/(\S*)$/.exec(beforeCaret);
  if (!match) return null;
  const start = beforeCaret.length - match[2].length - 1;
  return { query: match[2], start, end: caret };
}

const normalise = (text: string) =>
  text
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .trim();

/**
 * Skills whose name or label contains the query anywhere, so a fragment typed mid-word
 * (`ension`) still finds `pension_switch_review`. Case-insensitive, and underscores, hyphens and
 * spaces are interchangeable.
 */
export function matchSkills(skills: ChatSkill[], query: string): ChatSkill[] {
  const needle = normalise(query);
  if (!needle) return skills;
  return skills.filter(skill => normalise(`${skill.name} ${skillLabel(skill)}`).includes(needle));
}

export type SkillMenuRows = {
  matches: ChatSkill[];
  // "Create a new skill" stays reachable however the query is typed, so it is pinned after the
  // matches rather than filtered like the rest.
  createSkill: ChatSkill | null;
};

export function buildSkillMenuRows(skills: ChatSkill[], query: string): SkillMenuRows {
  const createSkill = skills.find(skill => skill.name === CREATE_SKILL_NAME) ?? null;
  const matches = matchSkills(skills, query).filter(skill => skill !== createSkill);
  return { matches, createSkill };
}

/** Every selectable row in display order: matches first, then the pinned create option. */
export function skillMenuOptions(rows: SkillMenuRows): ChatSkill[] {
  return rows.createSkill ? [...rows.matches, rows.createSkill] : rows.matches;
}

/** The message with the slash command removed, plus where the caret should land afterwards. */
export function removeSlashCommand(
  value: string,
  command: SlashCommandQuery,
): { value: string; caret: number } {
  const before = value.slice(0, command.start);
  const after = value.slice(command.end).replace(/^\s/, '');
  return { value: before + after, caret: before.length };
}
